import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import type { IThunkAPI } from '@/store/types';
import type {
  IVocabState,
  IVocabGigType,
  IVocabLanguage,
  IVocabGigLength,
  IVocabLanguageProficiency,
  IVocabLanguageProficiencyArea,
  IVocabProvince,
  IVocabHardwareRequirement,
  IVocabHardwareRequirementOption,
  IVocabCurrency,
  IVocabCampaignStatus,
  IVocabCourseDurationType,
  IVocabCourseClassType,
  IVocabCourseClassPlatform,
  IVocabGigPriority,
  // IVocabCourseClassStatus,
} from '@/store/slices/vocab/types';
import { vocabApi } from '@/api/vocab';
import { IVocabCountry } from '@/api/types';

export const initialState: IVocabState = {
  typeList: [],
  lengthList: [],
  languageList: [],
  languageProficiencyList: [],
  languageProficiencyAreaList: [],
  countryList: [],
  country: {
    id: 0,
    countryname: '',
    countrynameshort: '',
    bankrequirement: false,
    visibility: 0,
    phoneCountryCode: 0,
  },
  provinceList: [],
  provincesList: [],
  hardwareRequirementList: [],
  hardwareRequirementOptionList: [],
  currencyList: [],
  campaignStatusList: [],
  courseDurationTypeList: [],
  courseClassPlatformList: [],
  courseClassTypeList: [],
  courseClassStatusList: [],
  priorityList: [],
};

export const getVocabTypeList = createAsyncThunk<IVocabGigType[], void, IThunkAPI>(
  'get/vocab/type/list',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getTypesList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabType = createAsyncThunk<unknown, string, IThunkAPI>(
  'get/vocab/type',
  async (id: string, thunkAPI) => {
    try {
      const response = await vocabApi.getTypeById(id);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabLengthList = createAsyncThunk<IVocabGigLength[], void, IThunkAPI>(
  'get/vocab/length/list',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getLengthsList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabLength = createAsyncThunk<unknown, string, IThunkAPI>(
  'get/vocab/length',
  async (id: string, thunkAPI) => {
    try {
      const response = await vocabApi.getLengthById(id);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabLanguageList = createAsyncThunk<IVocabLanguage[], void, IThunkAPI>(
  'get/vocab/language',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getLanguageList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabLanguageProficiencyList = createAsyncThunk<IVocabLanguageProficiency[], void, IThunkAPI>(
  'get/vocab/languageProficiency',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getLanguageProficiencyList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabLanguageProficiencyAreaList = createAsyncThunk<IVocabLanguageProficiencyArea[], void, IThunkAPI>(
  'get/vocab/languageProficiencyArea',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getLanguageProficiencyAreaList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabCountryList = createAsyncThunk<IVocabCountry[], undefined, IThunkAPI>(
  'get/vocab/country/list',
  async (_, thunkAPI) => {
    try {
      // const source = axios.CancelToken.source();
      // thunkAPI.signal.addEventListener('abort', () => {
      //   source.cancel('getRestermReasonsList cancelled');
      // });
      const response = await vocabApi.getCountryList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabCountryById = createAsyncThunk<IVocabCountry, number, IThunkAPI>(
  'get/vocab/country',
  async (id, thunkAPI) => {
    try {
      const response = await vocabApi.getCountryById(id);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabProvincesList = createAsyncThunk<IVocabProvince[], undefined, IThunkAPI>(
  'get/vocab/province/list/',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getProvinceList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabProvinceListByCountryId = createAsyncThunk<IVocabProvince[], number, IThunkAPI>(
  'get/vocab/province/list/countryId',
  async (countryId, thunkAPI) => {
    try {
      // const source = axios.CancelToken.source();
      // thunkAPI.signal.addEventListener('abort', () => {
      //   source.cancel('getRestermReasonsList cancelled');
      // });
      const response = await vocabApi.getProvinceListByCountryId(countryId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabHardwareRequirementList = createAsyncThunk<IVocabHardwareRequirement[], undefined, IThunkAPI>(
  'get/vocab/list/hardwarerequirement',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getHardwareRequirementList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabHardwareRequirementOptionList = createAsyncThunk<
  IVocabHardwareRequirementOption[],
  undefined,
  IThunkAPI
>('get/vocab/list/hardwarerequirementoption', async (_, thunkAPI) => {
  try {
    const response = await vocabApi.getHardwareRequirementOptionList();
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getVocabCurrencyList = createAsyncThunk<IVocabCurrency[], undefined, IThunkAPI>(
  'get/vocab/currency/list',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getCurrencyList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabCampaignStatusList = createAsyncThunk<IVocabCampaignStatus[], undefined, IThunkAPI>(
  'get/vocab/campaignStatus/list',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getCampaignStatusList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabCourseDurationTypeList = createAsyncThunk<IVocabCourseDurationType[], undefined, IThunkAPI>(
  'get/vocab/coursedurationtype/list',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getCourseDurationTypeList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabCourseClassStatusList = createAsyncThunk<any, undefined, IThunkAPI>(
  'get/vocab/courseclassstatus/list',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getCourseClassStatusList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabCourseClassTypeList = createAsyncThunk<IVocabCourseClassType[], undefined, IThunkAPI>(
  'get/vocab/courseclasstype/list',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getCourseClassTypeList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabCourseClassPlatformList = createAsyncThunk<IVocabCourseClassPlatform[], undefined, IThunkAPI>(
  'get/vocab/courseclassplatform/list',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getCourseClassPlatformList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabGigPriorityList = createAsyncThunk<IVocabGigPriority[], undefined, IThunkAPI>(
  'get/vocab/vocabgigpriority/list',
  async (_, thunkAPI) => {
    try {
      const response = await vocabApi.getVocabGigPriorityList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const vocabSlice = createSlice({
  name: 'vocabGig',
  initialState,
  reducers: {
    clearTypeList(state) {
      state.typeList = [];
    },
    clearLengthList(state) {
      state.lengthList = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getVocabTypeList.fulfilled, (state, action) => {
        const sortedList = action.payload.sort((a: IVocabGigType, b: IVocabGigType) => {
          const fa = a.label.toLowerCase(),
            fb = b.label.toLowerCase();

          if (fa < fb) {
            return -1;
          }
          if (fa > fb) {
            return 1;
          }
          return 0;
        });
        state.typeList = sortedList;
      })
      .addCase(getVocabLengthList.fulfilled, (state, action) => {
        const sortedList = action.payload.sort((a: IVocabGigLength, b: IVocabGigLength) => {
          const fa = a.label.toLowerCase(),
            fb = b.label.toLowerCase();

          if (fa < fb) {
            return -1;
          }
          if (fa > fb) {
            return 1;
          }
          return 0;
        });
        state.lengthList = sortedList;
      })
      .addCase(getVocabLanguageList.fulfilled, (state, action) => {
        state.languageList = action.payload;
      })
      .addCase(getVocabLanguageProficiencyList.fulfilled, (state, action) => {
        state.languageProficiencyList = action.payload;
      })
      .addCase(getVocabLanguageProficiencyAreaList.fulfilled, (state, action) => {
        state.languageProficiencyAreaList = action.payload;
      })
      .addCase(getVocabCountryList.fulfilled, (state, action) => {
        state.countryList = action.payload;
      })
      .addCase(getVocabCountryById.fulfilled, (state, action) => {
        state.country = action.payload;
      })
      .addCase(getVocabProvincesList.fulfilled, (state, action) => {
        state.provincesList = action.payload;
      })
      .addCase(getVocabProvinceListByCountryId.fulfilled, (state, action) => {
        state.provinceList = action.payload;
      })
      .addCase(getVocabHardwareRequirementList.fulfilled, (state, action) => {
        state.hardwareRequirementList = action.payload;
      })
      .addCase(getVocabHardwareRequirementOptionList.fulfilled, (state, action) => {
        state.hardwareRequirementOptionList = action.payload;
      })
      .addCase(getVocabCurrencyList.fulfilled, (state, action) => {
        state.currencyList = action.payload;
      })
      .addCase(getVocabCampaignStatusList.fulfilled, (state, action) => {
        state.campaignStatusList = action.payload;
      })
      .addCase(getVocabCourseDurationTypeList.fulfilled, (state, action) => {
        state.courseDurationTypeList = action.payload;
      })
      .addCase(getVocabCourseClassStatusList.fulfilled, (state, action) => {
        state.courseClassStatusList = action.payload;
      })
      .addCase(getVocabCourseClassTypeList.fulfilled, (state, action) => {
        state.courseClassTypeList = action.payload;
      })
      .addCase(getVocabCourseClassPlatformList.fulfilled, (state, action) => {
        state.courseClassPlatformList = action.payload;
      })
      .addCase(getVocabGigPriorityList.fulfilled, (state, action) => {
        state.priorityList = action.payload;
      });
  },
});

// actions
export const { clearTypeList, clearLengthList } = vocabSlice.actions;

// selectors
export const typeList = (state: RootState): IVocabState['typeList'] => state.vocab.typeList;
export const lengthList = (state: RootState): IVocabState['lengthList'] => state.vocab.lengthList;
export const languageList = (state: RootState): IVocabState['languageList'] => state.vocab.languageList;
export const languageProficiencyList = (state: RootState): IVocabState['languageProficiencyList'] =>
  state.vocab.languageProficiencyList;
export const languageProficiencyAreaList = (state: RootState): IVocabState['languageProficiencyAreaList'] =>
  state.vocab.languageProficiencyAreaList;
export const countryList = (state: RootState): IVocabState['countryList'] => state.vocab.countryList;
export const country = (state: RootState): IVocabState['country'] => state.vocab.country;
export const provinceList = (state: RootState): IVocabState['provinceList'] => state.vocab.provinceList;
export const hardwareRequirementList = (state: RootState): IVocabState['hardwareRequirementList'] =>
  state.vocab.hardwareRequirementList;
export const hardwareRequirementOptionList = (state: RootState): IVocabState['hardwareRequirementOptionList'] =>
  state.vocab.hardwareRequirementOptionList;
export const currencyList = (state: RootState): IVocabState['currencyList'] => state.vocab.currencyList;
export const campaignStatusList = (state: RootState): IVocabState['campaignStatusList'] =>
  state.vocab.campaignStatusList;
export const courseDurationTypeList = (state: RootState): IVocabState['courseDurationTypeList'] =>
  state.vocab.courseDurationTypeList;
export const courseClassStatusList = (state: RootState): IVocabState['courseClassStatusList'] =>
  state.vocab.courseClassStatusList;
export const courseClassTypeList = (state: RootState): IVocabState['courseClassTypeList'] =>
  state.vocab.courseClassTypeList;
export const courseClassPlatformList = (state: RootState): IVocabState['courseClassPlatformList'] =>
  state.vocab.courseClassPlatformList;
export const priorityList = (state: RootState): IVocabState['priorityList'] => state.vocab.priorityList;

export default vocabSlice.reducer;
